import {Formik, Form, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import {TeamLeaderBase} from './type'
import toast from 'react-hot-toast'
import {createTeamLeader, updateTeamLeader} from './api'
import {handleApiError} from '../../../helpers/errorHandler'
import FormikControl from '../../../components/formikControl/FormikControl'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

interface TeamLeaderFormProps {
  initialValues?: TeamLeaderBase
  onAfterSubmit: () => void
}

const formInitialValues: Omit<TeamLeaderBase, 'teamleader_id' | 'nfc_code'> = {
  name: '',
  active_status: '1',
  gate_number: '',
}
const validationSchema: Yup.Schema<Omit<TeamLeaderBase, 'teamleader_id' | 'nfc_code'>> = Yup.object(
  {
    name: Yup.string()
      .max(100, 'The Team Leader name cannot exceed 100 characters')
      .required('Team Leader Name is required'),
    active_status: Yup.string()
      .oneOf(['0', '1'], 'Status must be either "0" or "1"')
      .required('Status is required'),
    gate_number: Yup.string().required('Gate Number is required'),
  }
)

function TeamLeaderForm({initialValues, onAfterSubmit}: TeamLeaderFormProps) {
  const formType = initialValues ? 'update' : 'add'

  const submitHandler = async (
    values: Omit<TeamLeaderBase, 'teamleader_id' | 'nfc_code'> | TeamLeaderBase,
    {setSubmitting}: FormikHelpers<any>
  ) => {
    const toastId = toast.loading('Submitting')
    const action = formType === 'add' ? createTeamLeader : updateTeamLeader

    try {
      await action(values as any)
      toast.success('Success', {id: toastId})
      onAfterSubmit()
    } catch (err: any) {
      handleApiError(err, toastId)
    } finally {
      setSubmitting(false)
    }
  }

  const formValues = initialValues || formInitialValues
  return (
    <div className="px-5 py-3">
      <Formik<Omit<TeamLeaderBase, 'teamleader_id' | 'nfc_code'>>
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, dirty, isValid, values}) => (
          <Form>
            <FormikControl
              control="input"
              required
              placeholder="Team Leader Name"
              label="Team Leader Name"
              name="name"
            />
            <FormikControl
              control="input"
              required
              placeholder="Gate Number"
              label="Gate Number"
              name="gate_number"
            />
            <FormikControl
              control="check"
              type="radio"
              options={[
                {key: 'Active', value: '1'},
                {key: 'Inactive', value: '0'},
              ]}
              required
              placeholder="Active Status"
              label="Active Status"
              name="active_status"
            />
            <div className="text-center pt-10">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

              <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default TeamLeaderForm
